import React from 'react';
import {
    Box,
    Button,
    Container,
    Paper,
    Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useNavigate, useParams } from 'react-router-dom';

// A dónde vuelve el comprador después de pagar.
//
// Estas rutas no existían: la preferencia de MercadoPago se creaba sin
// `back_urls`, así que el comprador quedaba varado en el sitio del proveedor y
// nunca volvía. Payway pide URLs de retorno sí o sí, y las mismas tres sirven
// para los dos proveedores.
//
// Sin guard de sesión a propósito: el comprador llega desde un sitio externo y
// puede no traer sesión ni call request activa.

const STATES = {
    exito: {
        icon: CheckCircleOutlineIcon,
        color: 'success.main',
        title: 'Recibimos tu pago',
        // El pago se confirma contra el proveedor, no con lo que diga la URL de
        // retorno: por eso "estamos confirmando" y no "listo".
        body:
            'Estamos confirmando la operación con el medio de pago. En unos segundos vas a ver tu pedido como pagado.',
    },
    pendiente: {
        icon: HourglassEmptyIcon,
        color: 'warning.main',
        title: 'Tu pago está en proceso',
        body:
            'Todavía no se acreditó. Cuando el medio de pago lo confirme, tu pedido pasa a pagado automáticamente.',
    },
    cancelado: {
        icon: HighlightOffIcon,
        color: 'error.main',
        title: 'Cancelaste el pago',
        body:
            'No se cobró nada. Podés volver a intentarlo desde tus órdenes cuando quieras.',
    },
};

const PaymentReturn = ({ estado }) => {
    const navigate = useNavigate();
    // Path param y no query string: Payway le pega su propio `?result=` a la
    // URL de retorno sin fijarse si ya tenía una.
    const { purchaseOrderId } = useParams();

    const state = STATES[estado] || STATES.pendiente;
    const Icon = state.icon;

    // Payway sólo acepta redirects http(s), así que el retorno siempre pasa por
    // acá aunque el comprador haya salido desde la app. El deep link se ofrece
    // como botón en vez de dispararse solo: un redirect automático le tira un
    // diálogo de "abrir la app" en la cara a quien pagó desde el navegador.
    const appLink = `videocallshopmobile://pago/${estado}${
        purchaseOrderId ? `?po=${purchaseOrderId}` : ''
    }`;

    // Si el comprador salió a pagar desde /call, la llamada sigue abierta en
    // la pestaña original (Call.js no se desmontó: el pago abrió otra). Lo que
    // corresponde es volver a esa pestaña — NO linkear a /call desde ésta, que
    // levantaría un segundo publisher en la misma sesión.
    const callInProgress = (() => {
        try {
            return Boolean(localStorage.getItem('CALL'));
        } catch (error) {
            return false;
        }
    })();

    return (
        <Container maxWidth="sm" sx={{ py: 6 }}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Icon sx={{ fontSize: 64, color: state.color, mb: 1 }} />

                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    {state.title}
                </Typography>

                <Typography color="text.secondary" sx={{ mb: callInProgress ? 2 : 3 }}>
                    {state.body}
                </Typography>

                {callInProgress && (
                    <Typography sx={{ fontWeight: 700, mb: 3 }}>
                        Tu videollamada sigue abierta en la otra pestaña. Cerrá
                        esta pestaña y volvé a ella.
                    </Typography>
                )}

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    }}
                >
                    <Button
                        variant="contained"
                        onClick={() => navigate('/account/orders?refresh=1')}
                    >
                        Ver mis órdenes
                    </Button>
                    <Button variant="outlined" href={appLink}>
                        Volver a la app
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default PaymentReturn;
